import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, HomeIcon } from '@heroicons/react/24/outline';
import SEO from './SEO';

const NotFound: React.FC = () => {
  return (
    <>
      {/* noIndex, and no canonical: this view is only ever reached on a URL that
          does not exist. Apache serves it with a real HTTP 404 (the route
          allowlist in public/.htaccess), so Google sees a 404, not a soft 404. */}
      <SEO
        noIndex
        title="404 - Page Not Found"
        description="The page you're looking for doesn't exist. Return to MyBank's homepage or explore our banking services."
      />
      
      <div data-no-popup className="min-h-screen bg-gradient-to-br from-[#001F5F] to-[#017EFF] flex items-center justify-center p-4">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/assets/grid.svg" 
            alt="Grid Pattern"
            className="w-full h-full object-cover"
          />
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative bg-white/10 backdrop-blur-xl p-8 rounded-2xl max-w-2xl w-full text-center"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <img 
              src="/assets/images/logo white blue light.webp" 
              alt="MyBank Logo" 
              className="h-16 mx-auto mb-8"
            />
            <h1 className="text-6xl font-bold text-white mb-4">
              404
            </h1>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Page Not Found
            </h2>
            <p className="text-xl text-white/90 mb-2">
              Sorry, the page you're looking for doesn't exist.
            </p>
            <p className="text-gray-200">
              It might have been moved, deleted, or you entered the wrong URL.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="space-y-6"
          >
            <div className="flex flex-col md:flex-row items-center justify-center gap-4">
              <Link 
                to="/"
                className="flex items-center gap-2 px-6 py-3 bg-white text-[#001F5F] rounded-lg hover:bg-opacity-90 transition-all font-semibold"
              >
                <HomeIcon className="w-5 h-5" />
                Go to Homepage
              </Link>
              <button 
                onClick={() => window.history.back()}
                className="flex items-center gap-2 px-6 py-3 border-2 border-white text-white rounded-lg hover:bg-white/10 transition-all font-semibold"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                Go Back
              </button>
            </div>
            
            <div className="pt-8 border-t border-white/20">
              <p className="text-white/80 mb-4">
                Popular pages you might be looking for:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                <Link 
                  to="/services"
                  className="flex items-center p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-all group"
                >
                  <div>
                    <h3 className="font-semibold text-white group-hover:text-[#017EFF] transition-colors">
                      Our Services
                    </h3>
                    <p className="text-sm text-white/70">
                      Banking solutions and products
                    </p>
                  </div>
                </Link>
                <Link 
                  to="/open-account"
                  className="flex items-center p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-all group"
                >
                  <div>
                    <h3 className="font-semibold text-white group-hover:text-[#017EFF] transition-colors">
                      Open Account
                    </h3>
                    <p className="text-sm text-white/70">
                      Start your banking journey
                    </p>
                  </div>
                </Link>
                <Link 
                  to="/who-we-are"
                  className="flex items-center p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-all group"
                >
                  <div>
                    <h3 className="font-semibold text-white group-hover:text-[#017EFF] transition-colors">
                      About Us
                    </h3>
                    <p className="text-sm text-white/70">
                      Learn about MyBank
                    </p>
                  </div>
                </Link>
                <Link 
                  to="/contact-us"
                  className="flex items-center p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-all group"
                >
                  <div>
                    <h3 className="font-semibold text-white group-hover:text-[#017EFF] transition-colors">
                      Contact Us
                    </h3>
                    <p className="text-sm text-white/70">
                      Get in touch with us
                    </p>
                  </div>
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </>
  );
};

export default NotFound;